import { DisplaySubMetric } from '@repo/types';
import {
  ContainerSizes,
  FAQ,
  PageContainer,
  PageHeader,
  Typography,
} from 'components';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import { getActionsForSubMetric } from 'helpers/submetrics/getActionsForSubMetric';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { buildMicrobiomeAgeBands, getAgeInYears } from '@repo/utils';
import { useRef } from 'react';
import { formatMicrobiomeAgeDelta } from 'services/microbiome/formatMicrobiomeAgeDelta';
import useMicrobiomeAgeRanges from 'services/microbiome/useMicrobiomeAgeRanges';
import { ActionPlanItem } from 'types/ActionPlan';
import AboutSection from '../../SubMetricData/AboutSection';
import ActionsReminderCard from '../../SubMetricData/ActionsReminderCard';
import AssociationsCard from '../../SubMetricData/AssociationsCard';
import MicrobiomeAgeOverTimeCard from '../../SubMetricGraphs/cards/MicrobiomeAgeOverTimeCard';
import ResultCard from '../../SubMetricGraphs/cards/ResultCard';
import { DefaultSubMetricDetail } from '../DefaultSubMetricDetail';
import { RelatedActions } from '../RelatedActions';
import FactorsSection from './FactorsSection';
import styles from './MicrobiomeAgeDetail.module.css';
import AdditionalResources from '../../SubMetricData/AdditionalResources';

type Props = {
  submetric: DisplaySubMetric;
  allActions: ActionPlanItem[];
};

export default function MicrobiomeAgeDetail({ submetric, allActions }: Props) {
  const actionItems = getActionsForSubMetric(
    allActions,
    submetric.gut_vaginal_health?.id,
  );
  const { currentKit, currentKitId } = useCurrentKitAndTinyAccount();
  const { data: rangesRow } = useMicrobiomeAgeRanges(currentKitId ?? '');
  const { trackEvent, trackEventOnce } = useAnalytics();
  const startAPRef = useRef(null);

  const trimmedFaq = submetric?.faq?.trim();

  const singleRangesRow = Array.isArray(rangesRow) ? rangesRow[0] : rangesRow;

  // No version-matched evaluation ranges means there's no microbiome-age data
  // for this kit, so fall back to the default metric page.
  if (!singleRangesRow) {
    return (
      <DefaultSubMetricDetail subMetric={submetric} allActions={allActions} />
    );
  }

  const bands = buildMicrobiomeAgeBands(singleRangesRow);

  // The shared userValueFormat keeps a decimal, so the headline is overridden
  // here; the RangesGraph marker still reads the exact submetric.user_value.
  const unit = submetric.gut_vaginal_health?.unit;
  const ageAtSampling = currentKit?.age_at_sampling;
  const subtitle =
    ageAtSampling != null
      ? `(${formatMicrobiomeAgeDelta(ageAtSampling, submetric.user_value)})`
      : undefined;
  const deltaYears =
    ageAtSampling != null
      ? getAgeInYears(ageAtSampling) - submetric.user_value
      : undefined;

  // Fire once per kit; we only reach here when actual microbiome-age data exists.
  trackEventOnce('Microbiome Age Page Viewed', currentKitId ?? '', {
    result_value: submetric.user_value,
    delta_years: deltaYears,
    evaluation_grade: submetric.evaluation_color,
  });

  return (
    <PageContainer size={ContainerSizes.LG} className='pt-3'>
      <Breadcrumb
        overrides={{
          [submetric.gut_vaginal_health?.id ?? '']: submetric?.name,
        }}
      />
      <PageHeader
        title={submetric?.name}
        beta={submetric?.gut_vaginal_health?.beta}
      />
      <TwoColumnGrid>
        <TwoColumnGrid.Wide>
          <section className={styles.sectionsContainer}>
            <ActionsReminderCard
              relatedActionsQty={actionItems.length}
              scrollTargetRef={startAPRef}
            />
            <ResultCard
              metric={submetric}
              ranges={bands}
              subtitle={subtitle}
              headline={
                <Typography variant='display-s'>
                  {`${Math.floor(submetric.user_value + 0.5)}${
                    unit ? ` ${unit}` : ''
                  }`}
                </Typography>
              }
            />
            <AssociationsCard
              metricName={submetric?.name ?? 'This metric'}
              associations={submetric.gut_vaginal_health_associations}
            />
            <RelatedActions
              actions={actionItems}
              className={'hide-on-desktop'}
            />
            <FactorsSection />
            {submetric.data_type !== 'string' && !!submetric.graph_display ? (
              <MicrobiomeAgeOverTimeCard metric={submetric} />
            ) : null}
            <AboutSection
              metric={submetric}
              onExpand={() => trackEvent('Microbiome Age Learn More Expanded')}
            />
            <AdditionalResources guides={submetric.gut_vaginal_health.guides} />
            {!!trimmedFaq ? (
              <FAQ
                faq={trimmedFaq}
                title='Frequently asked questions'
                className={styles.faq}
                hideLastDivider
                onQuestionExpand={question =>
                  trackEvent('Microbiome Age FAQ Expanded', {
                    faq_question: question,
                  })
                }
              />
            ) : null}
          </section>
        </TwoColumnGrid.Wide>
        <TwoColumnGrid.Slim>
          <section id='actions' ref={startAPRef}>
            <RelatedActions
              actions={actionItems}
              className={'hide-on-mobile'}
            />
          </section>
        </TwoColumnGrid.Slim>
      </TwoColumnGrid>
    </PageContainer>
  );
}
